import { useMemo } from "react";
import { Alert } from "react-bootstrap";

import {
  formatPageLabel,
  THEME_FOCUS_ID,
} from "../utils/content.utils";
import PageEditor from "./PageEditor";
import ThemeEditor from "./ThemeEditor";

const ContentForm = ({
  content,
  focusId,
  validationErrors,
  submitting,
  uploading,
  readOnly,
  submitLabel = "Update Content",
  submittingLabel = "Updating...",
  onSubmit,
  onContentChange,
  onUploadStart,
  onUploadEnd,
  onBack,
}) => {
  const pages = useMemo(
    () => (Array.isArray(content?.pages) ? content.pages : []),
    [content?.pages]
  );

  const focusTheme = !focusId || focusId === THEME_FOCUS_ID;
  const focusedPageIndex = focusTheme
    ? -1
    : pages.findIndex(
        (page, index) => (page?.id || `page-${index}`) === focusId
      );
  const focusedPage =
    focusedPageIndex >= 0 ? pages[focusedPageIndex] : null;

  const disabled = submitting || uploading || readOnly;

  const updateTheme = (theme) => {
    onContentChange({ ...content, theme });
  };

  const updatePage = (index, nextPage) => {
    const nextPages = pages.map((page, i) => (i === index ? nextPage : page));
    onContentChange({ ...content, pages: nextPages });
  };

  return (
    <form onSubmit={onSubmit}>
      {validationErrors?.content ? (
        <Alert variant="danger" className="mb-3">
          {validationErrors.content}
        </Alert>
      ) : null}

      {focusTheme ? (
        <ThemeEditor
          theme={content?.theme}
          disabled={disabled}
          onChange={updateTheme}
        />
      ) : null}

      {!focusTheme ? (
        focusedPage ? (
          <PageEditor
            page={focusedPage}
            disabled={disabled}
            onChange={(next) => updatePage(focusedPageIndex, next)}
            onUploadStart={onUploadStart}
            onUploadEnd={onUploadEnd}
          />
        ) : (
          <Alert variant="warning" className="mb-0">
            Page &quot;{formatPageLabel(focusId)}&quot; was not found in this
            content document.
          </Alert>
        )
      ) : null}

      <div
        className="d-flex gap-2 pt-3 mt-4 border-top sticky-bottom bg-white"
        style={{ bottom: 0, zIndex: 2, paddingBottom: 8 }}
      >
        <button
          type="submit"
          className="btn btn-primary"
          disabled={
            readOnly ||
            submitting ||
            uploading ||
            (!focusTheme && !focusedPage)
          }
        >
          {submitting
            ? submittingLabel
            : uploading
              ? "Uploading image..."
              : submitLabel}
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onBack}
          disabled={submitting}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default ContentForm;
